import {
  buildGroupedRows,
  isGroupHeaderRow,
  resolveGroupedRowsLoadMoreIndex,
  type GroupHeaderRowBase,
} from '@terra/ui-design-system';
import {
  SIGNAL_LIST_EMPTY_GROUP_KEY,
  type GroupedSignal,
  type SignalListGroupByField,
  type SignalListGroupCount,
} from '@terra/shared-contracts-signals';
import type {
  SignalsListGrouping,
  SignalsSortOrder,
} from '../../../store/tab/signalsTabScopedFilterStore';
import {
  DEFAULT_SIGNALS_SORT,
  SIGNALS_GROUP_COLUMN_ID_BY_FIELD,
  buildSignalsGridRows,
  resolveLatestSignalTrace,
  toSignalsSortColumnId,
  toSignalsSortField,
  type SignalsGridRow,
  type SignalsSort,
} from './signals-list.model';

export const SIGNALS_GROUP_HEADER_ID_PREFIX = '__signals_group__:';
export const SIGNALS_GROUP_HEADER_ROW_CLASS = 'signals-group-header-row';
export const SIGNALS_GROUP_SORTING_ORDER: SignalsSortOrder[] = ['asc', 'desc'];

export type SignalsGroupHeaderRow = GroupHeaderRowBase & {
  isCollapsed: boolean;
};

export type SignalsTableRow = SignalsGridRow | SignalsGroupHeaderRow;

export const isSignalsGroupHeaderRow = (
  row: SignalsTableRow | null | undefined,
): row is SignalsGroupHeaderRow => isGroupHeaderRow(row);

export const toSignalsGroupColumnId = (
  field: SignalListGroupByField,
): string | null => SIGNALS_GROUP_COLUMN_ID_BY_FIELD[field];

const toGroupKey = (value: string | null | undefined): string =>
  value || SIGNAL_LIST_EMPTY_GROUP_KEY;

export const getSignalsGroupKey = ({
  signal,
  groupByField,
}: {
  signal: GroupedSignal;
  groupByField: SignalListGroupByField;
}): string => {
  switch (groupByField) {
    case 'status':
      return toGroupKey(signal.status);
    case 'stage':
      return toGroupKey(signal.stage);
    case 'category':
      return toGroupKey(signal.category);
    case 'source':
      return toGroupKey(signal.source.sourceName);
    case 'endpoint':
      return toGroupKey(signal.isAppWide === true ? null : signal.endpointId);
    case 'execution':
      return toGroupKey(resolveLatestSignalTrace(signal)?.status);
    case 'finding':
      return toGroupKey(signal.linkedFinding?.severity);
    case 'asset':
      return toGroupKey(signal.assetId);
  }
};

export const buildSignalsTableRows = ({
  signals,
  expandedSignalIds,
  grouping,
  groups,
  collapsedGroupKeys,
  getGroupLabel,
}: {
  signals: readonly GroupedSignal[];
  expandedSignalIds: ReadonlySet<string>;
  grouping: SignalsListGrouping;
  groups: readonly SignalListGroupCount[];
  collapsedGroupKeys: ReadonlySet<string>;
  getGroupLabel: (groupKey: string) => string;
}): SignalsTableRow[] => {
  if (!grouping) {
    return buildSignalsGridRows({ signals, expandedSignalIds });
  }

  const countByKey = new Map(groups.map((group) => [group.key, group.count]));

  return buildGroupedRows<GroupedSignal, SignalsGroupHeaderRow>({
    items: signals,
    getItemId: (signal) => signal.id,
    getGroupKey: (signal) =>
      getSignalsGroupKey({ signal, groupByField: grouping.field }),
    getGroupLabel,
    collapsedGroupKeys,
    headerIdPrefix: SIGNALS_GROUP_HEADER_ID_PREFIX,
    knownGroupKeys: groups.map((group) => group.key),
    decorateHeader: (header) => ({
      ...header,
      count: Math.max(countByKey.get(header.groupKey) ?? 0, header.count),
      isCollapsed: collapsedGroupKeys.has(header.groupKey),
    }),
  }).flatMap((row): SignalsTableRow[] =>
    isGroupHeaderRow(row)
      ? [row]
      : buildSignalsGridRows({ signals: [row], expandedSignalIds }),
  );
};

export const resolveSignalsLoadMoreRowIndex = ({
  rows,
  grouping,
  collapsedGroupKeys,
  hasServerGroups,
  hasMore,
}: {
  rows: readonly SignalsTableRow[];
  grouping: SignalsListGrouping;
  collapsedGroupKeys: ReadonlySet<string>;
  hasServerGroups: boolean;
  hasMore: boolean;
}): number | null => {
  if (!grouping) {
    return rows.length > 0 ? rows.length - 1 : null;
  }

  return resolveGroupedRowsLoadMoreIndex({
    rows,
    collapsedGroupKeys,
    hasUnseenGroups: !hasServerGroups && hasMore,
  });
};

export type SignalsSortState = {
  sort: SignalsSort;
  grouping: SignalsListGrouping;
};

export type SignalsColumnSortState = {
  colId: string;
  sort: SignalsSortOrder;
  sortIndex: number;
};

export const resolveSignalsColumnSortState = ({
  sort,
  grouping,
}: SignalsSortState): SignalsColumnSortState[] => {
  const groupColumnId = grouping
    ? toSignalsGroupColumnId(grouping.field)
    : null;
  const groupState: SignalsColumnSortState[] =
    grouping && groupColumnId
      ? [{ colId: groupColumnId, sort: grouping.order, sortIndex: 0 }]
      : [];
  const sortColumnId = toSignalsSortColumnId(sort.field);
  const columnState: SignalsColumnSortState[] =
    sortColumnId && sortColumnId !== groupColumnId
      ? [
          {
            colId: sortColumnId,
            sort: sort.order,
            sortIndex: groupState.length,
          },
        ]
      : [];

  return [...groupState, ...columnState];
};

export const isSameSignalsSortState = ({
  left,
  right,
}: {
  left: SignalsSortState;
  right: SignalsSortState;
}): boolean =>
  left.sort.field === right.sort.field &&
  left.sort.order === right.sort.order &&
  left.grouping?.field === right.grouping?.field &&
  left.grouping?.order === right.grouping?.order;

export const resolveSignalsSortChange = ({
  sortedColumn,
  sort,
  grouping,
}: SignalsSortState & {
  sortedColumn: { colId: string; sort: SignalsSortOrder } | null;
}): SignalsSortState => {
  if (
    grouping &&
    sortedColumn?.colId === toSignalsGroupColumnId(grouping.field)
  ) {
    return { sort, grouping: { ...grouping, order: sortedColumn.sort } };
  }

  const field = sortedColumn ? toSignalsSortField(sortedColumn.colId) : null;

  return {
    sort:
      sortedColumn && field
        ? { field, order: sortedColumn.sort }
        : DEFAULT_SIGNALS_SORT,
    grouping,
  };
};
